import React, { useState, useEffect } from 'react';
import { 
  Shield, AlertTriangle, User, Users, Clock, Calendar, 
  Map, Home, Settings, Eye, FileText, 
  Bell, Wifi, Camera, Check, X, Info,
  Filter, PlusCircle, Download, Pencil, CheckCircle
} from 'lucide-react';

type ActiveTab = 'dashboard' | 'events' | 'zones' | 'history' | 'settings';

// Mock data for demonstration
const generateMockData = () => {
  // Generate some sample events
  const events = Array(10).fill(0).map((_, index) => ({
    id: `SEC-${index + 1000}`,
    timestamp: new Date(Date.now() - Math.random() * 86400000 * 3), // Up to 3 days ago
    type: ['UNKNOWN_OCCUPANT', 'UNUSUAL_ACTIVITY', 'UNEXPECTED_ENTRY', 'NIGHTTIME_MOVEMENT', 'VACANT_HOME_ACTIVITY'][Math.floor(Math.random() * 5)],
    severity: ['HIGH', 'MEDIUM', 'LOW'][Math.floor(Math.random() * 3)],
    status: ['ACTIVE', 'RESOLVED', 'IGNORED'][Math.floor(Math.random() * 3)],
    location: {
      zone: ['Entry Zone', 'Common Areas', 'Private Quarters', 'Perimeter'][Math.floor(Math.random() * 4)],
      room: ['Living Room', 'Kitchen', 'Master Bedroom', 'Guest Bedroom', 'Hallway', 'Entryway'][Math.floor(Math.random() * 6)],
      coordinates: {
        x: Math.floor(Math.random() * 100),
        y: Math.floor(Math.random() * 100)
      }
    },
    description: [
      'Unknown footprint pattern detected',
      'Unusual movement in vacant home',
      'Unexpected entry during away hours',
      'Nighttime activity detected in kitchen',
      'Multiple room transitions during sleep hours'
    ][Math.floor(Math.random() * 5)],
    detectedOccupants: {
      count: Math.floor(Math.random() * 3) + 1,
      types: ['KNOWN', 'UNKNOWN', 'UNCERTAIN']
    },
    sensorData: {
      pressure: Array(5).fill(0).map(() => Math.random() * 100),
      duration: Math.floor(Math.random() * 120) + 5 // 5-125 seconds
    }
  }));

  // Make sure we have at least 3 active events for demo purposes
  events[0].status = 'ACTIVE';
  events[1].status = 'ACTIVE';
  events[2].status = 'ACTIVE';
  events[0].severity = 'HIGH';
  events[1].severity = 'MEDIUM';

  // Generate some sample zones
  const zones = [
    {
      id: 'ZONE-1001',
      name: 'Entry Zone',
      type: 'ENTRY',
      rooms: ['Entryway', 'Hallway'],
      sensitivity: 'HIGH',
      activeTimes: ['MORNING', 'AFTERNOON', 'EVENING', 'NIGHT'],
      notifyOnTrigger: true,
      autoAlertAuthorities: false,
      knownOccupants: 3,
      pets: true,
      securityLevel: 'HIGH',
      lastUpdated: new Date(Date.now() - 1000000),
      coordinates: { x1: 10, y1: 70, x2: 30, y2: 90 },
      lastTriggered: null as Date | null
    },
    {
      id: 'ZONE-1002',
      name: 'Common Areas',
      type: 'INTERIOR',
      rooms: ['Living Room', 'Dining Room', 'Kitchen'],
      sensitivity: 'MEDIUM',
      activeTimes: ['EVENING', 'NIGHT'],
      notifyOnTrigger: true,
      autoAlertAuthorities: false,
      knownOccupants: 3,
      pets: true,
      securityLevel: 'MEDIUM',
      lastUpdated: new Date(Date.now() - 2000000),
      coordinates: { x1: 30, y1: 30, x2: 70, y2: 70 },
      lastTriggered: null as Date | null
    },
    {
      id: 'ZONE-1003',
      name: 'Private Quarters',
      type: 'SECURE',
      rooms: ['Master Bedroom', 'Guest Bedroom', 'Office', 'Bathroom'],
      sensitivity: 'HIGH',
      activeTimes: ['NIGHT'],
      notifyOnTrigger: true,
      autoAlertAuthorities: false,
      knownOccupants: 2,
      pets: false,
      securityLevel: 'HIGH',
      lastUpdated: new Date(Date.now() - 3000000),
      coordinates: { x1: 60, y1: 10, x2: 90, y2: 40 },
      lastTriggered: null as Date | null
    }
  ];

  // Give some zones recent triggers
  zones[0].lastTriggered = new Date(Date.now() - 3600000); // 1 hour ago
  zones[2].lastTriggered = new Date(Date.now() - 7200000); // 2 hours ago

  // Generate stats
  const activeEvents = events.filter(e => e.status === 'ACTIVE').length;
  const resolvedEvents = events.filter(e => e.status === 'RESOLVED').length;
  const falseAlarms = events.filter(e => e.status === 'IGNORED').length;

  const stats = {
    systemStatus: 'ARMED',
    activeEvents,
    resolvedEvents,
    falseAlarms,
    totalEvents: events.length,
    lastEvent: events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0].timestamp,
    triggersByZone: [
      { zoneName: 'Entry Zone', count: 4, trend: 5 },
      { zoneName: 'Common Areas', count: 3, trend: -2 },
      { zoneName: 'Private Quarters', count: 2, trend: 0 }
    ],
    triggersByTime: [
      { timeOfDay: 'MORNING', count: 2 },
      { timeOfDay: 'AFTERNOON', count: 1 },
      { timeOfDay: 'EVENING', count: 3 },
      { timeOfDay: 'NIGHT', count: 5 }
    ],
    unauthorizedEntries: 2,
    avgResponseTime: 7,
    knownOccupants: [
      { name: 'John Smith', lastSeen: new Date(Date.now() - 3600000), status: 'HOME' },
      { name: 'Emma Smith', lastSeen: new Date(Date.now() - 1800000), status: 'HOME' },
      { name: 'Lily Smith', lastSeen: new Date(Date.now() - 43200000), status: 'AWAY' }
    ],
    systemHealth: 95
  };

  return { events, zones, stats };
};

// Internal component for the dashboard
const SecurityDashboardTab: React.FC<{
  events: any[];
  zones: any[];
  stats: any;
  onResolveEvent: (eventId: string) => void;
  onIgnoreEvent: (eventId: string) => void;
}> = ({ events, zones, stats, onResolveEvent, onIgnoreEvent }) => {
  // Format timestamp as relative time
  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 60) {
      return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  };
  
  // Get active events
  const activeEvents = events
    .filter(event => event.status === 'ACTIVE')
    .slice(0, 3); // Show top 3
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-4">Security Dashboard</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold flex items-center">
            <Shield className="mr-2 text-blue-500" size={18} />
            System Status
          </h4>
          <div className="mt-2 text-2xl font-bold text-blue-600">{stats.systemStatus}</div>
          <p className="text-sm text-gray-600">Security system is active</p>
        </div>
        
        <div className="bg-red-50 p-4 rounded-lg">
          <h4 className="font-semibold flex items-center">
            <AlertTriangle className="mr-2 text-red-500" size={18} />
            Active Alerts
          </h4>
          <div className="mt-2 text-2xl font-bold text-red-600">{stats.activeEvents}</div>
          <p className="text-sm text-gray-600">{stats.resolvedEvents} resolved today</p>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg">
          <h4 className="font-semibold flex items-center">
            <Home className="mr-2 text-green-500" size={18} />
            Home Status
          </h4>
          <div className="mt-2 text-2xl font-bold text-green-600">
            {stats.knownOccupants.filter((o: {status: string}) => o.status === 'HOME').length} Occupants
          </div>
          <p className="text-sm text-gray-600">Home is currently occupied</p>
        </div>
      </div>
      
      <h4 className="text-md font-semibold mb-3">Active Security Events</h4>
      
      {activeEvents.length > 0 ? (
        <div className="space-y-3">
          {activeEvents.map(event => (
            <div key={event.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between">
                <div className="font-medium">{event.description}</div>
                <div className="text-sm text-gray-500">{formatTimestamp(event.timestamp)}</div>
              </div>
              <div className="mt-2 text-sm text-gray-600">
                Location: {event.location.room} ({event.location.zone})
              </div>
              <div className="mt-3 flex justify-end space-x-2">
                <button 
                  onClick={() => onResolveEvent(event.id)}
                  className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded hover:bg-green-200"
                >
                  Resolve
                </button>
                <button 
                  onClick={() => onIgnoreEvent(event.id)}
                  className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200"
                >
                  Ignore
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No active security events. Your home is secure.
        </div>
      )}
    </div>
  );
};

const IntelligentSecurityMonitor: React.FC = () => {
  // State
  const [activeTab, setActiveTab] = useState<ActiveTab>('dashboard');
  const [events, setEvents] = useState<any[]>([]);
  const [zones, setZones] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [liveMode, setLiveMode] = useState<boolean>(false);
  
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const data = generateMockData();
      setEvents(data.events);
      setZones(data.zones);
      setStats(data.stats);
      
      setLoading(false);
    };
    
    loadData();
  }, []);
  
  // Live mode data updates
  useEffect(() => {
    if (!liveMode) return;
    
    const interval = setInterval(() => {
      // Occasionally add new security events
      if (Math.random() < 0.3) { // 30% chance each interval
        const newEvent = {
          id: `SEC-${Math.floor(Math.random() * 1000) + 2000}`,
          timestamp: new Date(),
          type: ['UNKNOWN_OCCUPANT', 'UNUSUAL_ACTIVITY', 'UNEXPECTED_ENTRY'][Math.floor(Math.random() * 3)],
          severity: ['HIGH', 'MEDIUM', 'LOW'][Math.floor(Math.random() * 3)],
          status: 'ACTIVE',
          location: {
            zone: ['Entry Zone', 'Common Areas', 'Private Quarters'][Math.floor(Math.random() * 3)],
            room: ['Living Room', 'Kitchen', 'Master Bedroom', 'Hallway'][Math.floor(Math.random() * 4)],
            coordinates: {
              x: Math.floor(Math.random() * 100),
              y: Math.floor(Math.random() * 100)
            }
          },
          description: [
            'Unknown footprint pattern detected',
            'Unusual movement in vacant home',
            'Unexpected entry during away hours'
          ][Math.floor(Math.random() * 3)],
          detectedOccupants: {
            count: 1,
            types: ['UNKNOWN']
          },
          sensorData: {
            pressure: Array(5).fill(0).map(() => Math.random() * 100),
            duration: Math.floor(Math.random() * 120) + 5 // 5-125 seconds
          }
        };
        
        setEvents((prev: any[]) => [newEvent, ...prev]);
        
        // Update stats
        setStats((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            totalEvents: prev.totalEvents + 1,
            activeEvents: prev.activeEvents + 1
          };
        });
      }
    }, 8000); // Check every 8 seconds
    
    return () => clearInterval(interval);
  }, [liveMode]);
  
  // Handle resolving an event
  const handleResolveEvent = (eventId: string, notes?: string) => {
    setEvents((prev: any[]) => 
      prev.map(event => 
        event.id === eventId 
          ? {
              ...event,
              status: 'RESOLVED',
              resolutionNotes: notes || 'Resolved by user',
              resolvedAt: new Date()
            }
          : event
      )
    );
    
    // Update stats
    setStats((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        activeEvents: prev.activeEvents - 1,
        resolvedEvents: prev.resolvedEvents + 1
      };
    });
  };
  
  // Handle ignoring a false alarm
  const handleIgnoreEvent = (eventId: string) => {
    setEvents((prev: any[]) => 
      prev.map(event => 
        event.id === eventId 
          ? {
              ...event,
              status: 'IGNORED',
              resolutionNotes: 'Marked as false alarm',
              resolvedAt: new Date()
            }
          : event
      )
    );
    
    // Update stats
    setStats((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        activeEvents: prev.activeEvents - 1,
        falseAlarms: prev.falseAlarms + 1
      };
    });
  };
  
  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-gray-600">Loading security data...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold">Intelligent Security Monitor</h2>
          <p className="text-gray-600">
            Advanced floor-based security detection and monitoring system
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <div className="flex items-center">
            <span className={`mr-2 px-3 py-1 rounded-full text-xs font-medium ${liveMode ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
              {liveMode ? 'LIVE' : 'STATIC'}
            </span>
            <button
              onClick={() => setLiveMode(!liveMode)}
              className={`px-3 py-1 rounded-lg text-sm font-medium ${
                liveMode ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
              }`}
            >
              {liveMode ? 'Disable Live Updates' : 'Enable Live Updates'}
            </button>
          </div>
          <button
            onClick={() => {
              setLoading(true);
              setTimeout(() => {
                const data = generateMockData();
                setEvents(data.events);
                setZones(data.zones);
                setStats(data.stats);
                setLoading(false);
              }, 800);
            }}
            className="flex items-center px-3 py-1 bg-gray-100 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-200"
          >
            <div className="mr-1">↻</div> Refresh Data
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <ul className="flex flex-wrap -mb-px">
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`inline-flex items-center py-3 px-4 text-sm font-medium border-b-2 ${
                activeTab === 'dashboard'
                  ? 'text-blue-600 border-blue-500'
                  : 'border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <Home size={18} className="mr-2" />
              Dashboard
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('events')}
              className={`inline-flex items-center py-3 px-4 text-sm font-medium border-b-2 ${
                activeTab === 'events'
                  ? 'text-blue-600 border-blue-500'
                  : 'border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <AlertTriangle size={18} className="mr-2" />
              Security Events
              {stats && stats.activeEvents > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                  {stats.activeEvents}
                </span>
              )}
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('zones')}
              className={`inline-flex items-center py-3 px-4 text-sm font-medium border-b-2 ${
                activeTab === 'zones'
                  ? 'text-blue-600 border-blue-500'
                  : 'border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <Map size={18} className="mr-2" />
              Security Zones
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('history')}
              className={`inline-flex items-center py-3 px-4 text-sm font-medium border-b-2 ${
                activeTab === 'history'
                  ? 'text-blue-600 border-blue-500'
                  : 'border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <Clock size={18} className="mr-2" />
              History
            </button>
          </li>
          <li>
            <button
              onClick={() => setActiveTab('settings')}
              className={`inline-flex items-center py-3 px-4 text-sm font-medium border-b-2 ${
                activeTab === 'settings'
                  ? 'text-blue-600 border-blue-500'
                  : 'border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <Settings size={18} className="mr-2" />
              Settings
            </button>
          </li>
        </ul>
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && stats && (
        <SecurityDashboardTab 
          events={events} 
          zones={zones} 
          stats={stats}
          onResolveEvent={handleResolveEvent}
          onIgnoreEvent={handleIgnoreEvent}
        />
      )}
      
      {activeTab === 'events' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Security Events</h3>
          
          {/* Events Filter */}
          <div className="p-4 bg-gray-50 rounded-lg mb-6">
            <h4 className="text-md font-medium mb-3 flex items-center">
              <Filter size={16} className="mr-2 text-blue-500" />
              Filter Events
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
                <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm">
                  <option value="">All Types</option>
                  <option value="UNKNOWN_OCCUPANT">Unknown Occupant</option>
                  <option value="UNUSUAL_ACTIVITY">Unusual Activity</option>
                  <option value="UNEXPECTED_ENTRY">Unexpected Entry</option>
                  <option value="NIGHTTIME_MOVEMENT">Nighttime Movement</option>
                  <option value="VACANT_HOME_ACTIVITY">Vacant Home Activity</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm">
                  <option value="">All Severities</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm">
                  <option value="">All Locations</option>
                  <option value="Living Room">Living Room</option>
                  <option value="Kitchen">Kitchen</option>
                  <option value="Bedroom">Bedroom</option>
                  <option value="Hallway">Hallway</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm">
                  <option value="">All Statuses</option>
                  <option value="ACTIVE">Active</option>
                  <option value="RESOLVED">Resolved</option>
                  <option value="IGNORED">Ignored</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Events List */}
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Event</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Location</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Time</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {events.map(event => (
                  <tr key={event.id}>
                    <td className="py-4 pl-4 pr-3 text-sm">
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-2 ${
                          event.severity === 'HIGH' ? 'bg-red-500' : 
                          event.severity === 'MEDIUM' ? 'bg-yellow-500' : 'bg-green-500'
                        }`}></div>
                        <div>
                          <div className="font-medium text-gray-900">{event.description}</div>
                          <div className="text-gray-500">{event.type.replace(/_/g, ' ')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {event.location.room} ({event.location.zone})
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {new Date(event.timestamp).toLocaleString()}
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        event.status === 'ACTIVE' ? 'bg-red-100 text-red-800' : 
                        event.status === 'RESOLVED' ? 'bg-green-100 text-green-800' : 
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {event.status}
                      </span>
                    </td>
                    <td className="py-4 pl-3 pr-4 text-right text-sm font-medium">
                      {event.status === 'ACTIVE' && (
                        <div className="flex space-x-2 justify-end">
                          <button 
                            onClick={() => handleResolveEvent(event.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Resolve
                          </button>
                          <button 
                            onClick={() => handleIgnoreEvent(event.id)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Ignore
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {activeTab === 'zones' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Security Zones</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {zones.map(zone => (
              <div key={zone.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className={`px-4 py-3 ${
                  zone.securityLevel === 'HIGH' ? 'bg-red-50' : 
                  zone.securityLevel === 'MEDIUM' ? 'bg-yellow-50' : 'bg-green-50'
                }`}>
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium">{zone.name}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      zone.securityLevel === 'HIGH' ? 'bg-red-100 text-red-800' : 
                      zone.securityLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' : 
                      'bg-green-100 text-green-800'
                    }`}>
                      {zone.securityLevel} SECURITY
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="mb-4">
                    <div className="text-sm text-gray-500 mb-1">Rooms</div>
                    <div className="flex flex-wrap gap-1">
                      {zone.rooms.map((room: string, idx: number) => (
                        <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                          {room}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="mb-4">
                    <div className="text-sm text-gray-500 mb-1">Settings</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-xs">Sensitivity</div>
                        <div className="font-medium">{zone.sensitivity}</div>
                      </div>
                      <div>
                        <div className="text-xs">Notify on Trigger</div>
                        <div className="font-medium">{zone.notifyOnTrigger ? 'Yes' : 'No'}</div>
                      </div>
                      <div>
                        <div className="text-xs">Known Occupants</div>
                        <div className="font-medium">{zone.knownOccupants}</div>
                      </div>
                      <div>
                        <div className="text-xs">Alert Authorities</div>
                        <div className="font-medium">{zone.autoAlertAuthorities ? 'Yes' : 'No'}</div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Active Times</div>
                    <div className="flex flex-wrap gap-1">
                      {zone.activeTimes.map((time: string, idx: number) => (
                        <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                          {time}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      Edit Zone
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-6">
            <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              <PlusCircle size={16} className="mr-2" />
              Add New Security Zone
            </button>
          </div>
        </div>
      )}
      
      {activeTab === 'history' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Security Event History</h3>
          
          {/* History Filter */}
          <div className="p-4 bg-gray-50 rounded-lg mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                <select className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm">
                  <option>Last 7 days</option>
                  <option>Last 30 days</option>
                  <option>Last 90 days</option>
                  <option>Custom Range</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
                <select className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm">
                  <option>All Events</option>
                  <option>Resolved Events</option>
                  <option>Ignored Events</option>
                </select>
              </div>
              <div className="ml-auto">
                <button className="mt-5 inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <Download size={16} className="mr-1" />
                  Export
                </button>
              </div>
            </div>
          </div>
          
          {/* History Timeline */}
          <div className="flow-root">
            <ul className="-mb-8">
              {events
                .filter(event => event.status !== 'ACTIVE')
                .sort((a, b) => new Date(b.resolvedAt || 0).getTime() - new Date(a.resolvedAt || 0).getTime())
                .map((event, eventIdx) => (
                <li key={event.id}>
                  <div className="relative pb-8">
                    {eventIdx !== events.filter(e => e.status !== 'ACTIVE').length - 1 ? (
                      <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                    ) : null}
                    <div className="relative flex items-start space-x-3">
                      <div>
                        <div className={`relative p-1 rounded-full flex items-center justify-center ${
                          event.status === 'RESOLVED' ? 'bg-green-100' : 'bg-gray-100'
                        }`}>
                          {event.status === 'RESOLVED' ? (
                            <CheckCircle className="h-6 w-6 text-green-600" />
                          ) : (
                            <X className="h-6 w-6 text-gray-600" />
                          )}
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {event.status === 'RESOLVED' ? 'Resolved' : 'Ignored'}: {event.description}
                          </div>
                          <p className="mt-0.5 text-sm text-gray-500">
                            {new Date(event.resolvedAt || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="mt-2 bg-white rounded-md shadow-sm p-3 border border-gray-200">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-xs text-gray-500">Location</div>
                              <div className="text-sm">{event.location.room} ({event.location.zone})</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500">Event Type</div>
                              <div className="text-sm">{event.type.replace(/_/g, ' ')}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500">Severity</div>
                              <div className="text-sm">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  event.severity === 'HIGH' ? 'bg-red-100 text-red-800' : 
                                  event.severity === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' : 
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {event.severity}
                                </span>
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500">Resolution</div>
                              <div className="text-sm">{event.resolutionNotes || 'No notes'}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      
      {activeTab === 'settings' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Security Settings</h3>
          
          <div className="space-y-6">
            {/* Notification Settings */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-medium mb-3">Notification Preferences</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Email Notifications</div>
                    <div className="text-sm text-gray-500">Receive security alerts via email</div>
                  </div>
                  <div className="relative inline-block w-12 h-6 rounded-full bg-gray-200">
                    <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 transform"></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">SMS Notifications</div>
                    <div className="text-sm text-gray-500">Receive security alerts via text message</div>
                  </div>
                  <div className="relative inline-block w-12 h-6 rounded-full bg-green-500">
                    <div className="absolute left-7 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 transform"></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Push Notifications</div>
                    <div className="text-sm text-gray-500">Receive alerts on your mobile device</div>
                  </div>
                  <div className="relative inline-block w-12 h-6 rounded-full bg-green-500">
                    <div className="absolute left-7 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 transform"></div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Security Level Settings */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-medium mb-3">Security Level</h4>
              <div className="space-y-2">
                <div>
                  <label className="flex items-center">
                    <input 
                      type="radio" 
                      name="securityLevel" 
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      defaultChecked
                    />
                    <span className="ml-2 text-gray-700">High - Alert on all unusual activity</span>
                  </label>
                </div>
                <div>
                  <label className="flex items-center">
                    <input 
                      type="radio" 
                      name="securityLevel" 
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-gray-700">Medium - Alert on significant unusual activity</span>
                  </label>
                </div>
                <div>
                  <label className="flex items-center">
                    <input 
                      type="radio" 
                      name="securityLevel" 
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-gray-700">Low - Alert only on critical security events</span>
                  </label>
                </div>
              </div>
            </div>
            
            {/* Emergency Contacts */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-medium mb-3">Emergency Contacts</h4>
              <div className="space-y-3">
                <div className="p-3 bg-white rounded border border-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">Sarah Johnson</div>
                      <div className="text-sm text-gray-500">+1 (555) 123-4567</div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-500">
                      <Pencil size={16} />
                    </button>
                  </div>
                </div>
                <div className="p-3 bg-white rounded border border-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">Robert Smith</div>
                      <div className="text-sm text-gray-500">+1 (555) 987-6543</div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-500">
                      <Pencil size={16} />
                    </button>
                  </div>
                </div>
                <button className="w-full text-center py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  + Add Contact
                </button>
              </div>
            </div>
            
            {/* Save Settings */}
            <div className="flex justify-end">
              <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntelligentSecurityMonitor; 